import React, { useEffect, useState } from 'react';
import {
    Box, Button, FormControlLabel, InputAdornment, Switch, TextField, Typography, Paper} from '@mui/material';
import { describeMaxAlertPrice, isMaxAlertPriceDirty, parseMaxAlertPrice } from '../utils/maxAlertPrice';
import NotificationsIcon from '@mui/icons-material/Notifications';
import StoreIcon from '@mui/icons-material/Store';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

const NotificationPreferences = ({ 
    tracking, 
    onPreferenceChange, 
    disabled = false,
    mode = 'detailed',
    // Premium only (#357): the page passes a handler when the user may set a cap.
    onMaxPriceChange = null
}) => {
    const savedMaxPrice = tracking.maxAlertPrice ?? null;
    const [maxPriceInput, setMaxPriceInput] = useState(savedMaxPrice === null ? '' : String(savedMaxPrice));
    useEffect(() => {
        setMaxPriceInput(savedMaxPrice === null ? '' : String(savedMaxPrice));
    }, [savedMaxPrice]);

    const parsedMaxPrice = parseMaxAlertPrice(maxPriceInput);
    const maxPriceDirty = isMaxAlertPriceDirty(maxPriceInput, savedMaxPrice);

    const handleMaxPriceSave = () => {
        if (!parsedMaxPrice.error && onMaxPriceChange) {
            onMaxPriceChange(tracking.stockSku, parsedMaxPrice.value);
        }
    };

    const handleMaxPriceClear = () => {
        setMaxPriceInput('');
        if (onMaxPriceChange && savedMaxPrice !== null) {
            onMaxPriceChange(tracking.stockSku, null);
        }
    };

    const handleInStoreChange = (event) => {
        onPreferenceChange(
            tracking.stockSku, 
            event.target.checked, 
            tracking.emailOnlineNotifications
        );
    };

    const handleOnlineChange = (event) => {
        onPreferenceChange(
            tracking.stockSku, 
            tracking.emailInStoreNotifications, 
            event.target.checked
        );
    };

    const bothDisabled = !tracking.emailInStoreNotifications && !tracking.emailOnlineNotifications;
    const notificationsEnabled = tracking.emailInStoreNotifications || tracking.emailOnlineNotifications;

    const handleSingleChange = (event) => {
        const checked = event.target.checked;
        onPreferenceChange(
            tracking.stockSku,
            checked,
            checked
        );
    };

    return (
        <Paper elevation={1} sx={{ p: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <NotificationsIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight="500">
                    Email Notifications
                </Typography>
            </Box>

            {mode === 'single' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                        Email alerts enabled
                    </Typography>
                    <Switch
                        checked={notificationsEnabled}
                        onChange={handleSingleChange}
                        disabled={disabled}
                        size="small"
                        color="primary"
                    />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={tracking.emailInStoreNotifications}
                                onChange={handleInStoreChange}
                                disabled={disabled}
                                size="small"
                                color="primary"
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <StoreIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                <Typography variant="body2">
                                    In-Store Pickup Available
                                </Typography>
                            </Box>
                        }
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={tracking.emailOnlineNotifications}
                                onChange={handleOnlineChange}
                                disabled={disabled}
                                size="small"
                                color="primary"
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <ShoppingCartIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                <Typography variant="body2">
                                    Online Shipping Available
                                </Typography>
                            </Box>
                        }
                    />
                </Box>
            )}

            {onMaxPriceChange && (
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight="500" sx={{ mb: 1 }}>
                        Maximum price for alerts
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <TextField
                            value={maxPriceInput}
                            onChange={(event) => setMaxPriceInput(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && maxPriceDirty) {
                                    handleMaxPriceSave();
                                }
                            }}
                            placeholder="Any price"
                            size="small"
                            disabled={disabled}
                            error={Boolean(parsedMaxPrice.error)}
                            inputProps={{ inputMode: 'decimal', 'aria-label': 'Maximum price for alerts' }}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            sx={{ width: 150 }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleMaxPriceSave}
                            disabled={disabled || !maxPriceDirty || Boolean(parsedMaxPrice.error)}
                            sx={{ mt: '3px' }}
                        >
                            Save
                        </Button>
                        {savedMaxPrice !== null && (
                            <Button size="small" onClick={handleMaxPriceClear} disabled={disabled} sx={{ mt: '3px' }}>
                                Clear
                            </Button>
                        )}
                    </Box>
                    <Typography variant="caption" color={parsedMaxPrice.error ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 0.75 }}>
                        {parsedMaxPrice.error || describeMaxAlertPrice(savedMaxPrice)}
                    </Typography>
                </Box>
            )}

            {bothDisabled && (
                <Box 
                    sx={{ 
                        mt: 1.5, 
                        p: 1, 
                        bgcolor: 'warning.lighter',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'warning.main'
                    }}
                >
                    <Typography variant="caption" color="warning.dark" sx={{ display: 'flex', alignItems: 'center' }}>
                        ⚠️ All notifications are disabled for this GPU
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default NotificationPreferences;
